import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Users, CalendarDays, Clock3, MapPin, CheckCircle2, Circle, XCircle,
  ArrowRight, ArrowLeft, ShieldCheck, Sparkles, Plus, Pencil,
  Award, Info, Navigation, Loader2,
} from "lucide-react";
import CONFIG, { getUserCity } from "../../../config";
import { getServiceImage } from "../../../config/serviceImages";
import { KITCHEN_UTILITY_CLEANING_SCOPE } from "../../../config/pricing";
import { isUserLoggedIn, getAuthMobile, getAuthToken } from "../../common/Header";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";
const HERO_IMAGE   = getServiceImage("Kitchen Cleaning");
const REDIRECT_PATH = "/service/special-service/kitchen-utility-cleaning";

const fmt = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

const authHeaders = () => ({
  "Content-Type": "application/json",
  ...(getAuthToken() ? { Authorization: `Bearer ${getAuthToken()}` } : {}),
});

// ─────────────────────────────────────────────────────────────────────────────
// STATIC CONTENT
// ─────────────────────────────────────────────────────────────────────────────
const STEPS = [
  { key: "plan",     label: "Plan",           sub: "Standard or Premium"     },
  { key: "included", label: "What's Included", sub: "Expand to see details"  },
  { key: "schedule", label: "Schedule",       sub: "Date & time"             },
  { key: "address",  label: "Address",        sub: "Where to clean"          },
  { key: "review",   label: "Review & Pay",   sub: "Confirm & pay"           },
];

// Pricing (flat Standard/Premium plans, no home-size step) is admin-editable
// in config.js: CONFIG.SERVICE_CONFIG["special-services"].services["home-utility-cleaning"].
const HOME_UTILITY_CONFIG = CONFIG.SERVICE_CONFIG?.["special-services"]?.services?.["home-utility-cleaning"] || {};
const PLANS = HOME_UTILITY_CONFIG.plans || [];

const BOOKING_WINDOW_DAYS = HOME_UTILITY_CONFIG.bookingWindowDays || 6;

const TRUST_BULLETS = [
  { icon: Users,       text: "Professional Team"       },
  { icon: Sparkles,    text: "Cleaning Equipment Included" },
  { icon: ShieldCheck, text: "Eco-friendly Products"    },
  { icon: Award,       text: "Satisfaction Guarantee"   },
];

// Duration-based slots — pick how long the visit should be, then an exact
// time window within that duration. Same pattern/time windows as Rapid
// Cleaning's and Deep Cleaning's Schedule steps — duration here is a
// scheduling choice only, not a pricing lever (this service's price is
// already fully determined by the Standard/Premium plan from Step 1).
const DURATION_OPTIONS = [
  { id: "4hr", label: "4 Hours" },
  { id: "6hr", label: "6 Hours" },
];
const TIME_SLOTS_BY_DURATION = {
  "4hr": [
    { id: "4hr_0700_1100", label: "7:00 AM – 11:00 AM" },
    { id: "4hr_1200_1600", label: "12:00 PM – 4:00 PM" },
    { id: "4hr_1700_2100", label: "5:00 PM – 9:00 PM"  },
  ],
  "6hr": [
    { id: "6hr_0700_1300", label: "7:00 AM – 1:00 PM" },
    { id: "6hr_1400_2000", label: "2:00 PM – 8:00 PM" },
  ],
};

const ADDR_LABEL_ICONS = { Home: "🏠", Work: "🏢", Office: "🏢", Other: "📍" };

// Booking starts from tomorrow (no same-day slots) and stays open for
// BOOKING_WINDOW_DAYS days from there — both the chips and the custom
// date picker's min/max are driven off this same window.
const buildDateChips = () => {
  const chips = [];
  for (let i = 1; i <= BOOKING_WINDOW_DAYS; i++) {
    const d = new Date();
    d.setDate(d.getDate() + i);
    const iso = d.toISOString().slice(0, 10);
    const label = i === 1 ? "Tomorrow"
      : d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
    chips.push({ iso, label });
  }
  return chips;
};

const formatDateLabel = (iso) => {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00`);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" });
};

// ─────────────────────────────────────────────────────────────────────────────
const HomeUtilityCleaningPage = () => {
  const navigate = useNavigate();
  const mobile   = getAuthMobile();

  const [step, setStep] = useState("plan");
  const stepIndex = STEPS.findIndex((s) => s.key === step);

  const [city] = useState(getUserCity() || "bengaluru");

  // ── Step 1: Plan ──────────────────────────────────────────────────────────
  const [plan, setPlan] = useState("");

  const planMeta = PLANS.find((p) => p.id === plan) || null;
  const planPrice = planMeta?.price || 0;

  // ── Step 2: What's Included / Excluded — driven by the Step 1 plan ───────
  const scope = KITCHEN_UTILITY_CLEANING_SCOPE[plan] || null;

  // ── Step 3: Schedule ──────────────────────────────────────────────────────
  const dateChips = useMemo(() => buildDateChips(), []);
  const [selectedDate,   setSelectedDate]   = useState("");
  const [customDateOpen, setCustomDateOpen] = useState(false);
  const [duration,        setDuration]       = useState(""); // "4hr" | "6hr"
  const [slot,            setSlot]           = useState("");

  const durationMeta   = DURATION_OPTIONS.find((d) => d.id === duration) || null;
  const timeSlotChoices = TIME_SLOTS_BY_DURATION[duration] || [];
  const sessionMeta     = timeSlotChoices.find((t) => t.id === slot) || null;

  // Picking a different duration invalidates whatever specific time slot
  // was chosen under the old duration.
  const chooseDuration = (id) => { setDuration(id); setSlot(""); };

  // Estimated time comes straight from the selected plan (Standard/Premium
  // each have their own fixed duration — no home-size step anymore).
  const estimatedTime = planMeta?.duration || "";

  // ── Step 4: Address ───────────────────────────────────────────────────────
  const [addresses,        setAddresses]        = useState([]);
  const [loadingAddresses, setLoadingAddresses]  = useState(false);
  const [selectedAddressId, setSelectedAddressId] = useState("");
  const [showAddForm,      setShowAddForm]       = useState(false);
  const [savingAddr,       setSavingAddr]        = useState(false);
  const [addrFormError,    setAddrFormError]     = useState("");
  const [locatingMe,       setLocatingMe]        = useState(false);
  const [locationNote,     setLocationNote]      = useState("");
  const [instructions,     setInstructions]      = useState("");
  const [newAddr, setNewAddr] = useState({
    label: "Home", line1: "", area: "", city, state: "", pincode: "", map_url: "",
  });

  useEffect(() => {
    if (!mobile) return;
    let cancelled = false;
    (async () => {
      setLoadingAddresses(true);
      try {
        const res  = await fetch(`${API_BASE_URL}/api/user/user-addresses/${encodeURIComponent(mobile)}/`, { headers: authHeaders() });
        const data = await res.json();
        const list = Array.isArray(data?.data) ? data.data : [];
        if (!cancelled) {
          setAddresses(list);
          if (list.length) setSelectedAddressId(list[0].id);
        }
      } catch { /* saved addresses are optional — user can still add one manually */ }
      finally { if (!cancelled) setLoadingAddresses(false); }
    })();
    return () => { cancelled = true; };
  }, [mobile]);

  const newAddrAreaOptions = useMemo(() => {
    const entry = CONFIG.AREA_OPTIONS?.[newAddr.city] || CONFIG.AREA_OPTIONS?.[newAddr.city?.toLowerCase()];
    return entry?.areas || [];
  }, [newAddr.city]);

  const handleUseCurrentLocation = () => {
    setAddrFormError("");
    if (!navigator.geolocation) {
      setAddrFormError("Location access isn't supported by your browser. Please add your address manually.");
      return;
    }
    setLocatingMe(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        setNewAddr((prev) => ({ ...prev, map_url: `https://www.google.com/maps?q=${latitude},${longitude}` }));
        setShowAddForm(true);
        setLocationNote("📍 Location captured. Please confirm your address details below.");
        setLocatingMe(false);
      },
      () => {
        setAddrFormError("Couldn't access your location. Please add your address manually.");
        setLocatingMe(false);
      },
      { timeout: 8000 }
    );
  };

  const handleAddAddress = async () => {
    setAddrFormError("");
    if (!newAddr.line1.trim() || !newAddr.area || !newAddr.pincode.trim()) {
      setAddrFormError("Please fill in the house/flat details, area and pincode.");
      return;
    }
    if (addresses.length >= 3) {
      setAddrFormError("You already have 3 saved addresses — remove one from your profile first.");
      return;
    }
    if (!mobile) {
      navigate("/user/user-login", { state: { redirectTo: REDIRECT_PATH } });
      return;
    }
    const entry = {
      id: `addr-${Date.now()}`, label: newAddr.label, line1: newAddr.line1.trim(),
      area: newAddr.area, city: newAddr.city, state: newAddr.state.trim(),
      pincode: newAddr.pincode.trim(), map_url: newAddr.map_url || "",
    };
    const updated = [...addresses, entry];
    setSavingAddr(true);
    try {
      const res  = await fetch(`${API_BASE_URL}/api/user/user-addresses/${encodeURIComponent(mobile)}/`, {
        method: "PATCH", headers: authHeaders(), body: JSON.stringify({ addresses: updated }),
      });
      const data = await res.json();
      if (!res.ok || data?.success === false) throw new Error(data?.message || "Failed to save address.");
      const saved = Array.isArray(data?.data) ? data.data : updated;
      setAddresses(saved);
      const savedEntry = saved.find((a) => a.line1 === entry.line1 && a.pincode === entry.pincode) || saved[saved.length - 1];
      setSelectedAddressId(savedEntry?.id || entry.id);
      setShowAddForm(false);
      setLocationNote("");
      setNewAddr({ label: "Home", line1: "", area: "", city, state: "", pincode: "", map_url: "" });
    } catch (e) {
      setAddrFormError(e.message || "Failed to save address.");
    } finally {
      setSavingAddr(false);
    }
  };

  const selectedAddress = addresses.find((a) => a.id === selectedAddressId) || null;

  // ── Pricing (mirrors the standard checkout charge formula) ───────────────
  const CHARGES         = CONFIG.CHECKOUT_CHARGES ?? { DISCOUNT_PERCENT: 5, GST_PERCENT: 18, PLATFORM_FEE: 20 };
  const PARTIAL_THRESHOLD = CONFIG.PARTIAL_PAYMENT_THRESHOLD ?? 1000;
  const subtotal   = planPrice;
  const discount   = Math.round(subtotal * (CHARGES.DISCOUNT_PERCENT / 100));
  const taxableAmt = subtotal - discount;
  const gst         = Math.round(taxableAmt * (CHARGES.GST_PERCENT / 100));
  const platformFee = Number(CHARGES.PLATFORM_FEE ?? 0);
  const total        = taxableAmt + gst + platformFee;
  const canPayLater   = subtotal >= PARTIAL_THRESHOLD;

  const [paymentOption, setPaymentOption] = useState("pay_now");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [placing,       setPlacing]       = useState(false);
  const [placeError,    setPlaceError]    = useState("");

  useEffect(() => { if (!canPayLater) setPaymentOption("pay_now"); }, [canPayLater]);

  // ── Step validation / navigation ──────────────────────────────────────────
  const canContinue = {
    plan:     Boolean(plan),
    included: true,
    schedule: Boolean(selectedDate && duration && slot),
    address:  Boolean(selectedAddressId),
  };

  const goNext = () => {
    const idx = STEPS.findIndex((s) => s.key === step);
    if (!canContinue[step]) return;
    if (idx < STEPS.length - 1) setStep(STEPS[idx + 1].key);
  };
  const goBack = () => {
    const idx = STEPS.findIndex((s) => s.key === step);
    if (idx > 0) setStep(STEPS[idx - 1].key); else navigate(-1);
  };

  // ── Place order ────────────────────────────────────────────────────────────
  // Home & Utility Cleaning bookings are stored in their own collection
  // (special_service_homeUtilityCleaning) via a dedicated endpoint, not the
  // generic cart place-order pipeline — see book_home_utility_cleaning() /
  // home_utility_cleaning_booking_detail() in the backend. `items` /
  // `delivery_address` / `total_amount` below are display-only fields
  // consumed by PaymentPage.js's shared order-summary UI; the backend
  // reads `address`/`pricing` instead.
  const handlePlaceOrder = async () => {
    if (!agreedToTerms || placing) return;
    if (!isUserLoggedIn() || !mobile) {
      navigate("/user/user-login", { state: { redirectTo: REDIRECT_PATH } });
      return;
    }

    const item = {
      id: "home_utility_cleaning", service_id: "home_utility_cleaning",
      title: `Kitchen & Utensil Cleaning – ${planMeta?.name} Package`,
      name:  `Kitchen & Utensil Cleaning – ${planMeta?.name} Package`,
      price: subtotal, qty: 1,
      image: HERO_IMAGE,
    };

    const bookingPayload = {
      mobile,
      plan: { id: plan, name: planMeta?.name, price: planPrice, team: planMeta?.team, duration: planMeta?.duration },
      addons: [],
      schedule: {
        date: selectedDate, date_label: formatDateLabel(selectedDate),
        duration, duration_label: durationMeta?.label,
        slot, slot_label: sessionMeta?.label, time_range: sessionMeta?.label,
        team: planMeta?.team, estimated_time: estimatedTime,
      },
      address: selectedAddress || null,
      instructions,
      pricing: { subtotal, discount, gst, platform_fee: platformFee, total },
      payment_option: paymentOption,
      // Display-only for PaymentPage.js's shared order-summary UI:
      items: [item],
      delivery_address: selectedAddress || null,
      total_amount: total,
    };

    setPlacing(true);
    setPlaceError("");

    if (paymentOption === "pay_after_work") {
      try {
        const res  = await fetch(`${API_BASE_URL}/api/special-service/home-utility-cleaning/book/`, {
          method: "POST", credentials: "include", headers: authHeaders(), body: JSON.stringify(bookingPayload),
        });
        const data = await res.json();
        if (!res.ok || !data?.success) throw new Error(data?.message || "Failed to place booking.");
        navigate("/profile-dashboard", {
          replace: true,
          state: { openBookings: true, orderPlaced: true, orderId: data?.data?.process_id || "", total, mobile },
        });
      } catch (e) {
        setPlaceError(e.message || "Failed to place booking. Please try again.");
      } finally {
        setPlacing(false);
      }
      return;
    }

    try {
      const res  = await fetch(`${API_BASE_URL}/api/payment/create-order/`, {
        method: "POST", headers: authHeaders(), body: JSON.stringify({ amount: total, mobile }),
      });
      const data = await res.json();
      if (!res.ok || !data?.success) throw new Error(data?.message || "Could not initiate payment.");
      navigate("/payment", {
        state: {
          paymentOrder: data,
          orderPayload: { ...bookingPayload, __submit_endpoint: "/api/special-service/home-utility-cleaning/book/" },
          breakdown: { subtotal, discount, gst, platformFee, tip: 0, total },
        },
      });
    } catch (e) {
      setPlaceError(e.message || "Payment initiation failed. Please try again.");
    } finally {
      setPlacing(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div style={S.page}>
      <div style={S.container}>
        <div style={S.breadcrumb}>
          <Link to="/" style={S.breadcrumbLink}>Home</Link> <span>&gt;</span>{" "}
          <Link to="/services/special-services" style={S.breadcrumbLink}>Special Services</Link> <span>&gt;</span>{" "}
          <span>Kitchen &amp; Utensil Cleaning</span>
        </div>

        <div style={S.hero}>
          <div style={S.heroBadge}><Sparkles size={13} /> KITCHEN &amp; UTENSIL CLEANING</div>
          <h1 style={S.heroTitle}>Kitchen &amp; Utensil Cleaning</h1>
          <p style={S.heroText}>Deep kitchen degreasing, appliance cleaning and full utility-area reset — trained teams, transparent pricing.</p>
        </div>

        {/* Step progress */}
        <div style={S.stepRow}>
          {STEPS.map((s, i) => (
            <React.Fragment key={s.key}>
              <div style={S.stepItem}>
                <div style={{ ...S.stepCircle, ...(i === stepIndex ? S.stepCircleActive : {}), ...(i < stepIndex ? S.stepCircleDone : {}) }}>
                  {i < stepIndex ? <CheckCircle2 size={14} /> : i + 1}
                </div>
                <div>
                  <div style={{ ...S.stepLabel, ...(i <= stepIndex ? S.stepLabelActive : {}) }}>{s.label}</div>
                  <div style={S.stepSub}>{s.sub}</div>
                </div>
              </div>
              {i < STEPS.length - 1 && <div style={{ ...S.stepConnector, ...(i < stepIndex ? S.stepConnectorActive : {}) }} />}
            </React.Fragment>
          ))}
        </div>

        <button type="button" style={S.backBtn} onClick={goBack}>
          <ArrowLeft size={15} /> Back
        </button>

        {/* ── Step 1: Plan ─────────────────────────────────────────────── */}
        {step === "plan" && (
          <div style={S.card}>
            <h2 style={S.sectionTitle}>Choose Standard or Premium</h2>

            <div style={S.planGrid}>
              {PLANS.map((p) => {
                const selected = plan === p.id;
                const price = p.price;
                return (
                  <div key={p.id} style={{ ...S.planCard, ...(selected ? S.planCardActive : {}) }}>
                    {p.badge && <div style={S.planBadge}>{p.badge}</div>}
                    <div style={S.planName}>{p.name}</div>
                    <div style={S.planPrice}>{price != null ? fmt(price) : "—"}</div>
                    <div style={S.planMetaRow}>
                      <span style={S.planMetaItem}><Clock3 size={12} /> {p.duration}</span>
                      <span style={S.planMetaItem}><Users size={12} /> {p.team}</span>
                    </div>
                    <p style={S.planDesc}>{p.desc}</p>
                    <ul style={S.planFeatureList}>
                      {p.features.map((f) => (
                        <li key={f} style={S.planFeature}><CheckCircle2 size={14} color="#4f46e5" /> {f}</li>
                      ))}
                    </ul>
                    <button type="button" style={{ ...S.planSelectBtn, ...(selected ? S.planSelectBtnActive : {}) }} onClick={() => setPlan(p.id)}>
                      {selected ? "Selected" : "Select"}
                    </button>
                  </div>
                );
              })}
            </div>

            <div style={S.trustStrip}>
              {TRUST_BULLETS.map(({ icon: Icon, text }) => (
                <div key={text} style={S.trustItem}><Icon size={16} color="#4f46e5" /> {text}</div>
              ))}
            </div>

            <button type="button" style={{ ...S.continueBtn, ...(!canContinue.plan ? S.continueBtnDisabled : {}) }} disabled={!canContinue.plan} onClick={goNext}>
              Continue <ArrowRight size={16} />
            </button>
          </div>
        )}

        {/* ── Step 2: What's Included / Excluded (by plan) ────────────────── */}
        {step === "included" && (
          <div style={S.card}>
            <h2 style={S.sectionTitle}>{scope?.title || "What's Included"}</h2>
            <p style={S.scheduleHeadSub}>What's included and excluded for your selected plan.</p>

            {scope ? (
              <>
                <div style={S.doDontGrid}>
                  <div>
                    <div style={S.doDontColTitleInclude}><CheckCircle2 size={15} color="#4f46e5" /> Included</div>
                    <ul style={S.doDontList}>
                      {scope.included.map((p) => (
                        <li key={p} style={S.doDontItem}>
                          <CheckCircle2 size={13} color="#4f46e5" /> <span>{p}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <div style={S.doDontColTitleExclude}><XCircle size={15} color="#dc2626" /> Excluded</div>
                    <ul style={S.doDontList}>
                      {scope.excluded.map((p) => (
                        <li key={p} style={S.doDontItem}>
                          <XCircle size={13} color="#dc2626" /> <span>{p}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div style={S.notesBox}>
                  <div style={S.notesBoxTitle}><Info size={15} color="#4f46e5" /> Customer Notes</div>
                  <ul style={S.notesList}>
                    {scope.customerNotes.map((n) => (
                      <li key={n} style={S.notesItem}>{n}</li>
                    ))}
                  </ul>
                </div>
              </>
            ) : (
              <p style={S.scheduleHeadSub}>Go back and choose Standard or Premium to see what's included.</p>
            )}

            <button type="button" style={S.continueBtn} onClick={goNext}>
              Continue <ArrowRight size={16} />
            </button>
          </div>
        )}

        {/* ── Step 3: Schedule ──────────────────────────────────────────── */}
        {step === "schedule" && (
          <div style={S.card}>
            <h2 style={S.sectionTitle}>When would you like us to come?</h2>
            <p style={S.scheduleHeadSub}>Bookable from tomorrow, up to {BOOKING_WINDOW_DAYS} days ahead.</p>

            <div style={S.fieldGroup}>
              <div style={S.chipRow}>
                {dateChips.map((c) => (
                  <button key={c.iso} type="button" style={{ ...S.dateChip, ...(selectedDate === c.iso ? S.dateChipActive : {}) }}
                    onClick={() => { setSelectedDate(c.iso); setCustomDateOpen(false); }}>
                    {c.label}
                  </button>
                ))}
                <button type="button" style={{ ...S.dateChip, ...(customDateOpen ? S.dateChipActive : {}) }} onClick={() => setCustomDateOpen((v) => !v)}>
                  <CalendarDays size={14} /> Pick a Date
                </button>
              </div>
              {customDateOpen && (
                <input
                  type="date" style={S.dateInput}
                  min={dateChips[0]?.iso}
                  max={dateChips[dateChips.length - 1]?.iso}
                  value={!dateChips.some((c) => c.iso === selectedDate) ? selectedDate : ""}
                  onChange={(e) => setSelectedDate(e.target.value)}
                />
              )}
            </div>

            <div style={S.fieldGroup}>
              <div style={S.fieldLabel}>Select Duration</div>
              <div style={S.chipRow}>
                {DURATION_OPTIONS.map((d) => (
                  <button key={d.id} type="button" style={{ ...S.dateChip, ...(duration === d.id ? S.dateChipActive : {}) }}
                    onClick={() => chooseDuration(d.id)}>
                    {d.label}
                  </button>
                ))}
              </div>
            </div>

            {duration && (
              <div style={S.fieldGroup}>
                <div style={S.fieldLabel}>Select Time Slot</div>
                <div style={S.slotGrid}>
                  {timeSlotChoices.map((t) => (
                    <button key={t.id} type="button" style={{ ...S.slotChip, ...(slot === t.id ? S.slotChipActive : {}) }} onClick={() => setSlot(t.id)}>
                      <div style={{ ...S.slotChipLabel, ...(slot === t.id ? { color: "#fff" } : {}) }}>{t.label}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {plan && (
              <div style={S.scheduleInfoRow}>
                <span style={S.scheduleInfoChip}><Users size={14} /> {planMeta?.team}</span>
                <span style={S.scheduleInfoChip}><Clock3 size={14} /> Estimated time: {estimatedTime}</span>
              </div>
            )}

            <button type="button" style={{ ...S.continueBtn, ...(!canContinue.schedule ? S.continueBtnDisabled : {}) }} disabled={!canContinue.schedule} onClick={goNext}>
              Continue <ArrowRight size={16} />
            </button>
          </div>
        )}

        {/* ── Step 4: Address ───────────────────────────────────────────── */}
        {step === "address" && (
          <div style={S.card}>
            <h2 style={S.sectionTitle}>Where should we provide the service?</h2>

            <button type="button" style={S.locationBtn} onClick={handleUseCurrentLocation} disabled={locatingMe}>
              {locatingMe ? <Loader2 size={15} /> : <Navigation size={15} />}
              {locatingMe ? "Locating…" : "Use my current location"}
            </button>
            {locationNote && <div style={S.locationNote}><Info size={13} /> {locationNote}</div>}

            {!mobile ? (
              <div style={S.stateBox}>
                <Link to="/user/user-login" state={{ redirectTo: REDIRECT_PATH }} style={S.inlineLink}>Log in</Link> to use a saved address, or add one below.
              </div>
            ) : loadingAddresses ? (
              <div style={S.stateBox}>Loading your addresses…</div>
            ) : addresses.length > 0 && (
              <div style={S.fieldGroup}>
                <div style={S.fieldLabel}>Saved Addresses</div>
                <div style={S.addressList}>
                  {addresses.map((addr) => {
                    const selected = selectedAddressId === addr.id;
                    return (
                      <button key={addr.id} type="button" style={{ ...S.addressRow, ...(selected ? S.addressRowActive : {}) }} onClick={() => setSelectedAddressId(addr.id)}>
                        {selected ? <CheckCircle2 size={18} color="#4f46e5" /> : <Circle size={18} color="#d1d5db" />}
                        <span style={S.addressIcon}>{ADDR_LABEL_ICONS[addr.label] || "📍"}</span>
                        <div style={S.addressBody}>
                          <div style={S.addressLabel}>{addr.label}</div>
                          <div style={S.addressText}>{[addr.line1, addr.area, addr.city].filter(Boolean).join(", ")}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {!showAddForm ? (
              <button type="button" style={S.addAddressBtn} onClick={() => setShowAddForm(true)}>
                <Plus size={15} /> Add New Address
              </button>
            ) : (
              <div style={S.addAddressForm}>
                <div style={S.fieldLabel}>New Address</div>
                <div style={S.radioGrid3}>
                  {["Home", "Work", "Other"].map((l) => (
                    <button key={l} type="button" style={{ ...S.radioCard, ...(newAddr.label === l ? S.radioCardActive : {}) }} onClick={() => setNewAddr((p) => ({ ...p, label: l }))}>
                      {ADDR_LABEL_ICONS[l]} {l}
                    </button>
                  ))}
                </div>
                <input style={S.input} placeholder="House / Flat No., Building, Street" value={newAddr.line1}
                  onChange={(e) => setNewAddr((p) => ({ ...p, line1: e.target.value }))} />
                <div style={S.formRow2}>
                  <select style={S.select} value={newAddr.area} onChange={(e) => setNewAddr((p) => ({ ...p, area: e.target.value }))}>
                    <option value="">Select area</option>
                    {newAddrAreaOptions.map((a) => <option key={a} value={a}>{a}</option>)}
                  </select>
                  <input style={S.input} placeholder="Pincode" value={newAddr.pincode}
                    onChange={(e) => setNewAddr((p) => ({ ...p, pincode: e.target.value }))} />
                </div>
                <input style={S.input} placeholder="State (optional)" value={newAddr.state}
                  onChange={(e) => setNewAddr((p) => ({ ...p, state: e.target.value }))} />
                {addrFormError && <div style={S.errorText}>{addrFormError}</div>}
                <div style={S.formActions}>
                  <button type="button" style={S.secondaryBtn} onClick={() => { setShowAddForm(false); setAddrFormError(""); }}>Cancel</button>
                  <button type="button" style={S.primaryBtnSm} onClick={handleAddAddress} disabled={savingAddr}>
                    {savingAddr ? "Saving…" : "Save Address"}
                  </button>
                </div>
              </div>
            )}

            <div style={S.fieldGroup}>
              <div style={S.fieldLabel}>Special Instructions (optional)</div>
              <textarea style={S.textarea} rows={3} placeholder="Gate code, parking info, areas to focus on…" value={instructions}
                onChange={(e) => setInstructions(e.target.value)} />
            </div>

            <button type="button" style={{ ...S.continueBtn, ...(!canContinue.address ? S.continueBtnDisabled : {}) }} disabled={!canContinue.address} onClick={goNext}>
              Continue <ArrowRight size={16} />
            </button>
          </div>
        )}

        {/* ── Step 5: Review & Pay ──────────────────────────────────────── */}
        {step === "review" && (
          <div style={S.reviewLayout}>
            <div style={S.reviewLeft}>
              <div style={S.recapCard}>
                <div style={S.recapHead}>
                  <div style={S.recapTitle}><Sparkles size={16} color="#4f46e5" /> Plan</div>
                  <button type="button" style={S.changeLink} onClick={() => setStep("plan")}><Pencil size={12} /> Change</button>
                </div>
                <div style={S.recapText}>{planMeta?.name} Package — {fmt(planPrice)}</div>
                <div style={S.recapSubText}><Users size={13} /> {planMeta?.team} &nbsp;·&nbsp; <Clock3 size={13} /> {estimatedTime}</div>
              </div>

              <div style={S.recapCard}>
                <div style={S.recapHead}>
                  <div style={S.recapTitle}><CalendarDays size={16} color="#4f46e5" /> Date &amp; Time</div>
                  <button type="button" style={S.changeLink} onClick={() => setStep("schedule")}><Pencil size={12} /> Change</button>
                </div>
                <div style={S.recapText}>
                  {formatDateLabel(selectedDate)} · {durationMeta?.label} · {sessionMeta?.label}
                </div>
                <div style={S.recapSubText}><Users size={13} /> {planMeta?.team} &nbsp;·&nbsp; <Clock3 size={13} /> {estimatedTime}</div>
              </div>

              <div style={S.recapCard}>
                <div style={S.recapHead}>
                  <div style={S.recapTitle}><MapPin size={16} color="#4f46e5" /> Address</div>
                  <button type="button" style={S.changeLink} onClick={() => setStep("address")}><Pencil size={12} /> Change</button>
                </div>
                <div style={S.recapText}>
                  {selectedAddress ? [selectedAddress.line1, selectedAddress.area, selectedAddress.city, selectedAddress.pincode].filter(Boolean).join(", ") : "No address selected"}
                </div>
                {instructions && <div style={S.recapSubText}>Note: {instructions}</div>}
              </div>

              <div style={S.recapCard}>
                <div style={S.fieldLabel}>Payment</div>
                <div style={S.paymentOptions}>
                  <button type="button" style={{ ...S.payOptionBtn, ...(paymentOption === "pay_now" ? S.payOptionBtnActive : {}) }} onClick={() => setPaymentOption("pay_now")}>
                    {paymentOption === "pay_now" ? <CheckCircle2 size={16} color="#4f46e5" /> : <Circle size={16} color="#d1d5db" />}
                    <div>
                      <div style={S.payOptionTitle}>Pay Now</div>
                      <div style={S.payOptionSub}>Pay the full amount upfront</div>
                    </div>
                  </button>
                  <button type="button" disabled={!canPayLater}
                    style={{ ...S.payOptionBtn, ...(paymentOption === "pay_after_work" ? S.payOptionBtnActive : {}), ...(!canPayLater ? S.payOptionBtnDisabled : {}) }}
                    onClick={() => canPayLater && setPaymentOption("pay_after_work")}>
                    {paymentOption === "pay_after_work" ? <CheckCircle2 size={16} color="#4f46e5" /> : <Circle size={16} color="#d1d5db" />}
                    <div>
                      <div style={S.payOptionTitle}>Pay After Work</div>
                      <div style={S.payOptionSub}>{canPayLater ? "Pay once the job is done" : `Available above ${fmt(PARTIAL_THRESHOLD)}`}</div>
                    </div>
                  </button>
                </div>
              </div>

              {placeError && <div style={S.errorBox}>{placeError}</div>}

              <label style={S.termsWrap}>
                <input type="checkbox" checked={agreedToTerms} onChange={(e) => setAgreedToTerms(e.target.checked)} />
                <span>
                  I agree to the{" "}
                  <a href="/legal-information" style={S.inlineLink}>Terms &amp; Conditions</a> and{" "}
                  <a href="/legal-information" style={S.inlineLink}>Privacy Policy</a>
                </span>
              </label>
            </div>

            <div style={S.reviewRight}>
              <div style={S.priceCard}>
                <img src={HERO_IMAGE} alt="Kitchen & Utensil Cleaning" style={S.priceCardImg} />
                <div style={S.priceCardTitle}>{planMeta?.name} Package</div>

                <div style={S.billRow}><span>Plan ({planMeta?.name})</span><span>{fmt(planPrice)}</span></div>
                <div style={S.billDivider} />
                <div style={S.billRow}><span>Subtotal</span><span>{fmt(subtotal)}</span></div>
                <div style={S.billRow}><span>Discount ({CHARGES.DISCOUNT_PERCENT}%)</span><span style={{ color: "#16a34a" }}>-{fmt(discount)}</span></div>
                <div style={S.billRow}><span>GST + Other Charges</span><span>{fmt(gst + platformFee)}</span></div>
                <div style={S.billDivider} />
                <div style={S.totalRow}><span>Total Payable</span><span>{fmt(total)}</span></div>

                <button type="button" style={{ ...S.continueBtn, ...((!agreedToTerms || placing) ? S.continueBtnDisabled : {}) }}
                  disabled={!agreedToTerms || placing} onClick={handlePlaceOrder}>
                  {placing ? "Placing Order…" : paymentOption === "pay_now" ? "Proceed to Payment" : "Confirm Booking"}
                  {!placing && <ArrowRight size={16} />}
                </button>
                <div style={S.secureNote}><ShieldCheck size={13} color="#4f46e5" /> Secure checkout • Verified professionals • No hidden charges</div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// STYLES — indigo (#4f46e5) accent, matching the home page and Deep
// Cleaning's shared brand color.
// ─────────────────────────────────────────────────────────────────────────────
const S = {
  page:      { background: "#fafafa", minHeight: "100vh", padding: "28px 20px 60px", fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif", color: "#111827" },
  container: { maxWidth: "1100px", margin: "0 auto" },
  breadcrumb:     { fontSize: "13px", color: "#9ca3af", marginBottom: "22px", display: "flex", gap: "6px" },
  breadcrumbLink: { color: "#9ca3af", textDecoration: "none" },

  hero:      { textAlign: "center", marginBottom: "28px" },
  heroBadge: { display: "inline-flex", alignItems: "center", gap: "6px", background: "#eef2ff", color: "#4f46e5", fontSize: "12px", fontWeight: 800, letterSpacing: "0.05em", padding: "6px 12px", borderRadius: "999px", marginBottom: "12px" },
  heroTitle: { margin: "0 0 8px", fontSize: "32px", fontWeight: 900 },
  heroText:  { margin: 0, fontSize: "15px", color: "#6b7280" },

  stepRow: { display: "flex", alignItems: "flex-start", justifyContent: "center", gap: "4px", margin: "0 0 24px", flexWrap: "wrap" },
  stepItem: { display: "flex", alignItems: "center", gap: "8px" },
  stepCircle: { width: "30px", height: "30px", borderRadius: "50%", background: "#e5e7eb", color: "#6b7280", fontSize: "12px", fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  stepCircleActive: { background: "#4f46e5", color: "#fff" },
  stepCircleDone:   { background: "#eef2ff", color: "#4f46e5" },
  stepLabel: { fontSize: "13px", fontWeight: 700, color: "#9ca3af", lineHeight: 1.3 },
  stepLabelActive: { color: "#111827" },
  stepSub: { fontSize: "11px", color: "#9ca3af" },
  stepConnector: { width: "34px", height: "2px", background: "#e5e7eb", marginTop: "15px", flexShrink: 0 },
  stepConnectorActive: { background: "#4f46e5" },

  backBtn: { display: "inline-flex", alignItems: "center", gap: "6px", background: "none", border: "none", color: "#4f46e5", fontWeight: 700, fontSize: "13px", cursor: "pointer", marginBottom: "16px", padding: 0 },

  card: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: "20px", padding: "26px", maxWidth: "760px", margin: "0 auto" },
  sectionTitle: { margin: "0 0 20px", fontSize: "22px", fontWeight: 800 },
  scheduleHeadSub: { margin: "-14px 0 20px", fontSize: "13px", color: "#6b7280" },

  fieldGroup: { marginBottom: "22px" },
  fieldLabel: { fontSize: "13px", fontWeight: 700, color: "#374151", marginBottom: "10px", textTransform: "uppercase", letterSpacing: "0.03em" },

  radioGrid3: { display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: "10px" },
  radioCard: { display: "flex", alignItems: "center", gap: "8px", padding: "12px 14px", borderRadius: "12px", border: "1.5px solid #e5e7eb", background: "#fff", fontSize: "13px", fontWeight: 600, color: "#374151", cursor: "pointer", textAlign: "left" },
  radioCardActive: { borderColor: "#4f46e5", background: "#eef2ff", color: "#3730a3" },

  select: { width: "100%", padding: "12px 14px", borderRadius: "12px", border: "1.5px solid #e5e7eb", fontSize: "14px", background: "#fff", color: "#111827" },
  input: { width: "100%", padding: "12px 14px", borderRadius: "12px", border: "1.5px solid #e5e7eb", fontSize: "14px", marginBottom: "10px", boxSizing: "border-box" },
  textarea: { width: "100%", padding: "12px 14px", borderRadius: "12px", border: "1.5px solid #e5e7eb", fontSize: "14px", resize: "vertical", boxSizing: "border-box", fontFamily: "inherit" },
  formRow2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "10px" },

  continueBtn: { width: "100%", marginTop: "6px", padding: "15px 0", borderRadius: "12px", background: "#4f46e5", color: "#fff", fontSize: "15px", fontWeight: 800, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" },
  continueBtnDisabled: { opacity: 0.45, cursor: "not-allowed" },

  doDontGrid: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: "20px", marginBottom: "20px" },
  doDontColTitleInclude: { display: "flex", alignItems: "center", gap: "6px", fontSize: "14px", fontWeight: 800, color: "#4f46e5", marginBottom: "10px" },
  doDontColTitleExclude: { display: "flex", alignItems: "center", gap: "6px", fontSize: "14px", fontWeight: 800, color: "#dc2626", marginBottom: "10px" },
  doDontList: { margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: "8px", listStyle: "none" },
  doDontItem: { display: "flex", alignItems: "flex-start", gap: "8px", fontSize: "13px", color: "#374151", lineHeight: 1.5 },
  notesBox: { background: "#f8fbff", border: "1px solid #e7eef9", borderRadius: "14px", padding: "16px", marginBottom: "16px" },
  notesBoxTitle: { display: "flex", alignItems: "center", gap: "6px", fontSize: "14px", fontWeight: 800, color: "#111827", marginBottom: "10px" },
  notesList: { margin: 0, padding: "0 0 0 18px", display: "flex", flexDirection: "column", gap: "8px", fontSize: "13px", color: "#374151", lineHeight: 1.5 },
  notesItem: { paddingLeft: "2px" },

  planGrid: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: "16px", marginBottom: "18px" },
  planCard: { position: "relative", border: "1.5px solid #e5e7eb", borderRadius: "16px", padding: "20px", background: "#fff" },
  planCardActive: { borderColor: "#4f46e5", boxShadow: "0 0 0 3px #eef2ff" },
  planBadge: { position: "absolute", top: "-10px", right: "16px", background: "#4f46e5", color: "#fff", fontSize: "11px", fontWeight: 800, padding: "4px 10px", borderRadius: "999px" },
  planName: { fontSize: "16px", fontWeight: 800, marginBottom: "4px" },
  planPrice: { fontSize: "26px", fontWeight: 900, color: "#4f46e5", marginBottom: "8px" },
  planMetaRow: { display: "flex", gap: "12px", fontSize: "11px", fontWeight: 700, color: "#6b7280", marginBottom: "10px" },
  planMetaItem: { display: "inline-flex", alignItems: "center", gap: "4px" },
  planDesc: { fontSize: "12px", color: "#6b7280", lineHeight: 1.5, margin: "0 0 14px" },
  planFeatureList: { listStyle: "none", padding: 0, margin: "0 0 16px", display: "flex", flexDirection: "column", gap: "8px" },
  planFeature: { display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", color: "#374151" },
  planSelectBtn: { width: "100%", padding: "11px 0", borderRadius: "10px", border: "1.5px solid #4f46e5", background: "#fff", color: "#4f46e5", fontWeight: 700, fontSize: "13px", cursor: "pointer" },
  planSelectBtnActive: { background: "#4f46e5", color: "#fff" },

  trustStrip: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: "10px", marginBottom: "22px" },
  trustItem: { display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", fontWeight: 600, color: "#4b5563", background: "#f8fafc", border: "1px solid #eef2f7", borderRadius: "10px", padding: "10px 12px" },

  chipRow: { display: "flex", flexWrap: "wrap", gap: "8px" },
  dateChip: { display: "inline-flex", alignItems: "center", gap: "6px", padding: "10px 16px", borderRadius: "999px", border: "1.5px solid #e5e7eb", background: "#fff", fontSize: "13px", fontWeight: 700, color: "#374151", cursor: "pointer" },
  dateChipActive: { borderColor: "#4f46e5", background: "#4f46e5", color: "#fff" },
  dateInput: { marginTop: "10px", padding: "10px 14px", borderRadius: "10px", border: "1.5px solid #e5e7eb", fontSize: "13px" },

  slotGrid: { display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: "10px" },
  slotChip: { padding: "14px 0", borderRadius: "12px", border: "1.5px solid #e5e7eb", background: "#fff", color: "#374151", cursor: "pointer", textAlign: "center" },
  slotChipActive: { borderColor: "#4f46e5", background: "#4f46e5", color: "#fff" },
  slotChipLabel: { fontSize: "13px", fontWeight: 700, color: "inherit" },

  scheduleInfoRow: { display: "flex", gap: "10px", marginBottom: "22px", flexWrap: "wrap" },
  scheduleInfoChip: { display: "inline-flex", alignItems: "center", gap: "6px", background: "#eef2ff", color: "#3730a3", fontSize: "12px", fontWeight: 700, padding: "8px 12px", borderRadius: "999px" },

  locationBtn: { display: "inline-flex", alignItems: "center", gap: "8px", padding: "12px 18px", borderRadius: "12px", border: "1.5px solid #4f46e5", background: "#eef2ff", color: "#4f46e5", fontWeight: 700, fontSize: "13px", cursor: "pointer", marginBottom: "10px" },
  locationNote: { display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", color: "#4f46e5", marginBottom: "18px" },

  addressList: { display: "flex", flexDirection: "column", gap: "10px" },
  addressRow: { display: "flex", alignItems: "center", gap: "10px", padding: "14px", borderRadius: "12px", border: "1.5px solid #e5e7eb", background: "#fff", cursor: "pointer", textAlign: "left" },
  addressRowActive: { borderColor: "#4f46e5", background: "#eef2ff" },
  addressIcon: { fontSize: "18px" },
  addressBody: { flex: 1 },
  addressLabel: { fontSize: "13px", fontWeight: 700, color: "#111827" },
  addressText: { fontSize: "12px", color: "#6b7280" },

  addAddressBtn: { display: "inline-flex", alignItems: "center", gap: "6px", background: "none", border: "1.5px dashed #c7d2fe", color: "#4f46e5", fontWeight: 700, fontSize: "13px", cursor: "pointer", padding: "12px 16px", borderRadius: "12px", width: "100%", justifyContent: "center", margin: "12px 0 22px" },
  addAddressForm: { border: "1.5px solid #e5e7eb", borderRadius: "14px", padding: "16px", marginBottom: "22px" },
  formActions: { display: "flex", gap: "10px", justifyContent: "flex-end" },
  secondaryBtn: { padding: "10px 16px", borderRadius: "10px", border: "1.5px solid #e5e7eb", background: "#fff", color: "#374151", fontWeight: 700, fontSize: "13px", cursor: "pointer" },
  primaryBtnSm: { padding: "10px 18px", borderRadius: "10px", border: "none", background: "#4f46e5", color: "#fff", fontWeight: 700, fontSize: "13px", cursor: "pointer" },
  errorText: { fontSize: "12px", color: "#dc2626", marginBottom: "10px" },
  errorBox: { background: "#fef2f2", border: "1px solid #fecaca", color: "#dc2626", fontSize: "13px", padding: "12px 14px", borderRadius: "10px", marginBottom: "16px" },
  stateBox: { fontSize: "13px", color: "#6b7280", background: "#f8fafc", border: "1px solid #eef2f7", borderRadius: "10px", padding: "12px 14px", marginBottom: "16px" },
  inlineLink: { color: "#4f46e5", fontWeight: 700, textDecoration: "none" },

  reviewLayout: { display: "grid", gridTemplateColumns: "1.5fr 1fr", gap: "22px", alignItems: "start" },
  reviewLeft: { display: "flex", flexDirection: "column", gap: "14px" },
  reviewRight: { position: "sticky", top: "20px" },

  recapCard: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: "16px", padding: "16px 18px" },
  recapHead: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px" },
  recapTitle: { display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", fontWeight: 800, color: "#111827" },
  changeLink: { display: "inline-flex", alignItems: "center", gap: "4px", background: "none", border: "none", color: "#4f46e5", fontWeight: 700, fontSize: "12px", cursor: "pointer", padding: 0 },
  recapText: { fontSize: "13px", color: "#374151", lineHeight: 1.5 },
  recapSubText: { display: "flex", alignItems: "center", gap: "4px", fontSize: "12px", color: "#6b7280", marginTop: "6px" },

  paymentOptions: { display: "flex", flexDirection: "column", gap: "10px" },
  payOptionBtn: { display: "flex", alignItems: "center", gap: "10px", padding: "12px 14px", borderRadius: "12px", border: "1.5px solid #e5e7eb", background: "#fff", cursor: "pointer", textAlign: "left" },
  payOptionBtnActive: { borderColor: "#4f46e5", background: "#eef2ff" },
  payOptionBtnDisabled: { opacity: 0.5, cursor: "not-allowed" },
  payOptionTitle: { fontSize: "13px", fontWeight: 700, color: "#111827" },
  payOptionSub: { fontSize: "11px", color: "#6b7280" },

  termsWrap: { display: "flex", alignItems: "flex-start", gap: "8px", fontSize: "12px", color: "#6b7280", lineHeight: 1.5 },

  priceCard: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: "18px", padding: "18px", overflow: "hidden" },
  priceCardImg: { width: "100%", aspectRatio: "1 / 1", objectFit: "cover", borderRadius: "12px", marginBottom: "12px" },
  priceCardTitle: { fontSize: "15px", fontWeight: 800, marginBottom: "12px" },
  billRow: { display: "flex", justifyContent: "space-between", fontSize: "13px", color: "#374151", marginBottom: "10px" },
  billDivider: { borderTop: "1px solid #e5e7eb", margin: "10px 0" },
  totalRow: { display: "flex", justifyContent: "space-between", fontSize: "18px", fontWeight: 900, marginBottom: "16px" },
  secureNote: { display: "flex", alignItems: "center", gap: "6px", justifyContent: "center", marginTop: "12px", fontSize: "11px", color: "#6b7280", textAlign: "center" },
};

export default HomeUtilityCleaningPage;
